import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { pushSelectItem } from '../utils/gtm';

const ProductCard = ({ product, onClick }) => (
  <button
    onClick={onClick}
    className="group shrink-0 w-36 sm:w-48 md:w-56 text-left snap-start"
  >
    <div
      className="relative rounded-2xl overflow-hidden bg-gray-100 shadow-sm group-hover:shadow-xl transition-shadow duration-300"
      style={{ aspectRatio: '4/5' }}
    >
      {product.primary_image ? (
        <img
          src={product.primary_image}
          srcSet={product.primary_image_srcset || undefined}
          sizes="(min-width: 768px) 224px, (min-width: 640px) 192px, 144px"
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
          <Package className="w-10 h-10 text-gray-300" />
        </div>
      )}
    </div>
    <div className="mt-2.5 px-0.5">
      <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
      <p className="text-sm font-semibold text-gray-900 mt-0.5">৳{Number(product.price).toLocaleString()}</p>
    </div>
  </button>
);

const BestSellingProducts = ({ items }) => {
  const navigate = useNavigate();
  const scrollerRef = useRef(null);

  if (!items || items.length === 0) return null;

  const scrollBy = (amount) => {
    scrollerRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-5 sm:mb-7">Best Sellers</h2>

      <div className="relative">
        {items.length > 1 && (
          <>
            <button
              onClick={() => scrollBy(-320)}
              className="hidden md:flex absolute -left-4 top-[38%] -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 text-gray-600 hover:text-primary-600 transition-colors"
              aria-label="Scroll best sellers left"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => scrollBy(320)}
              className="hidden md:flex absolute -right-4 top-[38%] -translate-y-1/2 z-10 items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 text-gray-600 hover:text-primary-600 transition-colors"
              aria-label="Scroll best sellers right"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none' }}
        >
          {items.map((item, index) => (
            <ProductCard
              key={item.id}
              product={item.product}
              onClick={() => {
                pushSelectItem(
                  {
                    id: item.product.id,
                    name: item.product.name,
                    category: item.product.category_name || '',
                    subCategory: '',
                    price: item.product.price,
                  },
                  { listId: 'best_sellers', listName: 'Best Sellers', index }
                );
                navigate(`/product/${item.product.id}`);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BestSellingProducts;
